import { useEffect, useMemo, useState } from "react";
import type { MailWindowStats } from "@feedback-hub/contract/browser";

import { ApiError, getMailStats, type MailAppStats } from "../../api.js";
import type { SortDir, SortValue } from "../../sort.js";
import { count, isFlagged, pct } from "./mailFormat.js";
import { SortableTh } from "./SortableTh.js";
import { useSort } from "./useSort.js";

type Key = "app" | "sends" | "deliveries" | "bounces" | "complaints" | "bounceRate" | "complaintRate";
interface Row {
  appId: string;
  w: MailWindowStats;
}

const DIRS: Record<Key, SortDir> = {
  app: "asc",
  sends: "desc",
  deliveries: "desc",
  bounces: "desc",
  complaints: "desc",
  bounceRate: "desc",
  complaintRate: "desc",
};

function value(r: Row, key: Key): SortValue {
  switch (key) {
    case "app":
      return r.appId;
    case "sends":
      return r.w.sends;
    case "deliveries":
      return r.w.deliveries;
    case "bounces":
      return r.w.bounces;
    case "complaints":
      return r.w.complaints;
    case "bounceRate":
      return r.w.bounceRate;
    case "complaintRate":
      return r.w.complaintRate;
  }
}

export function AppStatsTab() {
  const [apps, setApps] = useState<MailAppStats[]>([]);
  const [windowDays, setWindowDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getMailStats();
        if (!cancelled) setApps(res.apps);
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Failed to load mail stats.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const rows = useMemo<Row[]>(
    () =>
      apps.flatMap((a) =>
        a.windows.filter((w) => w.windowDays === windowDays).map((w) => ({ appId: a.appId, w })),
      ),
    [apps, windowDays],
  );
  const { sorted, sort, onSort } = useSort<Row, Key>(rows, value, { key: "sends", dir: "desc" }, DIRS);

  if (loading) return <p className="muted">Loading mail stats…</p>;
  if (error) return <p className="error">{error}</p>;

  const th = (label: string, k: Key) => (
    <SortableTh label={label} sortKey={k} activeKey={sort.key} dir={sort.dir} onSort={onSort} />
  );

  return (
    <div className="panel section">
      <div className="filters" style={{ marginBottom: "0.75rem" }}>
        <div className="field">
          <label htmlFor="mail-window">Window</label>
          <select
            id="mail-window"
            value={windowDays}
            onChange={(e) => setWindowDays(Number(e.target.value))}
          >
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
          </select>
        </div>
      </div>
      {sorted.length === 0 ? (
        <p className="muted">No apps to show.</p>
      ) : (
        <div className="table-wrap">
          <table className="queue">
            <thead>
              <tr>
                {th("App", "app")}
                {th("Sent", "sends")}
                {th("Delivered", "deliveries")}
                {th("Bounced", "bounces")}
                {th("Complained", "complaints")}
                {th("Bounce rate", "bounceRate")}
                {th("Complaint rate", "complaintRate")}
              </tr>
            </thead>
            <tbody>
              {sorted.map(({ appId, w }) => (
                <tr key={`${appId}-${w.windowDays}`} style={{ cursor: "default" }}>
                  <td className="mono">{appId}</td>
                  <td>{count(w.sends)}</td>
                  <td>{count(w.deliveries)}</td>
                  <td>{count(w.bounces)}</td>
                  <td>{count(w.complaints)}</td>
                  <td>{pct(w.bounceRate)}</td>
                  <td>
                    {pct(w.complaintRate)}
                    {isFlagged(w) && (
                      <span className="warn-mark" title="At or above the complaint-rate threshold">
                        high
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
